import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import { getSelectedOrderIsPreSubmission } from 'entities/Order/model/selectors/ordersSelectors';
import { Order } from 'entities/Order/model/types/OrderSchema';
import {
    HydratedProposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { getSupplierQuantities } from 'features/EditOrderItemSourcing';

import { columnStyles, tableStyles } from './OrderViewItemSupplierTable.styles';
import { OrderViewItemSupplierTableRow } from './components/OrderViewItemSupplierTableRow';
import { OrderViewItemSupplierTableSkeleton } from './components/OrderViewItemSupplierTableSkeleton';

type OrderViewItemSupplierTableProps = {
    loadingRequiredData: boolean;
    selectedItem: CatalogItem;
    selectedOrder: Order;
    proposals: HydratedProposal[];
};

export const OrderViewItemSupplierTable = memo(
    ({
        selectedItem,
        selectedOrder,
        proposals,
        loadingRequiredData,
    }: OrderViewItemSupplierTableProps) => {
        const { t } = useTranslation();

        const supplierQuantities = useSelector(getSupplierQuantities);
        const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
        const canSeePrices = useSelector(getUserCanSeePrices);
        const canEditQty = useSelector(getCanEditCountOfQty);
        const isMaintenanceTechnician = useSelector(
            getUserIsMaintenanceTechnician,
        );
        const showPrices = canSeePrices && !isMaintenanceTechnician;
        const showPicker = isPreSubmission && canEditQty;

        const getSupplierQuantity = useCallback(
            (proposalUid: string) => {
                return (
                    supplierQuantities.find(
                        (p: SelectedProposal) => p.uid === proposalUid,
                    )?.selectedQuantity || 0
                );
            },
            [supplierQuantities],
        );

        // If there are no proposals, don't show the table at all
        if (proposals.length === 0) {
            return null;
        }

        // If we're still loading the required data, show the loading skeleton
        if (loadingRequiredData || !selectedItem.isCheckFinished) {
            return (
                <OrderViewItemSupplierTableSkeleton
                    t={t}
                    proposals={proposals}
                />
            );
        }

        return (
            <TableContainer>
                <Table
                    sx={tableStyles}
                    data-testid="order-view-item-supplier-table"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className="table-suppliers table-left"
                                sx={columnStyles.suppliers}
                            >
                                {t('Suppliers')}
                            </TableCell>
                            {showPrices && (
                                <TableCell sx={columnStyles.unitCost}>
                                    {t('Unit cost')}
                                </TableCell>
                            )}
                            {isPreSubmission && (
                                <TableCell sx={columnStyles.stock}>
                                    {t('Stock')}
                                </TableCell>
                            )}
                            <TableCell
                                sx={{
                                    ...columnStyles.qty,
                                    textAlign: showPicker ? 'center' : 'right',
                                }}
                            >
                                {t('Qty')}
                            </TableCell>
                            {showPrices && (
                                <TableCell sx={columnStyles.total}>
                                    {t('Total')}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {proposals.map((proposal) => (
                            <OrderViewItemSupplierTableRow
                                key={proposal.seller.uid}
                                proposal={proposal}
                                selectedItem={selectedItem}
                                selectedOrder={selectedOrder}
                                getSupplierQuantity={getSupplierQuantity}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    },
);
